<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="retention"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <div class="pi-toolbar">
          <div class="pi-toolbar__field">
            <span><span :id="ids.type">Coorte de</span> <pi-definition-tooltip info-key="retention.cohortType" label="Coorte de"/></span>
            <div class="pi-toggle" role="group" :aria-labelledby="ids.type">
              <button v-for="option in types" :key="option.value" type="button" class="pi-toggle__btn"
                      :class="{ 'is-on': choice.type === option.value }"
                      :aria-pressed="choice.type === option.value ? 'true' : 'false'"
                      @click="selectType(option.value)">{{ option.label }}</button>
            </div>
          </div>
          <div class="pi-toolbar__field">
            <span><span :id="ids.granularity">Granularidade</span> <pi-definition-tooltip info-key="retention.granularity" label="Granularidade"/></span>
            <div class="pi-toggle" role="group" :aria-labelledby="ids.granularity">
              <button v-for="option in granularities" :key="option.value" type="button" class="pi-toggle__btn"
                      :class="{ 'is-on': choice.granularity === option.value }"
                      :aria-pressed="choice.granularity === option.value ? 'true' : 'false'"
                      :disabled="!allowed(choice.type, option.value)"
                      :title="allowed(choice.type, option.value) ? null : companyGranularityText"
                      :aria-describedby="choice.type === 'company' ? ids.granularityHint : null"
                      @click="selectGranularity(option.value)">{{ option.label }}</button>
            </div>
          </div>
          <pi-definition-tooltip :info-key="retentionInfoKey" :version="definitionVersion" align="left"/>
          <pi-export-screen class="pi-ret__export" screen="Retenção" :filters="exportFilters" :period="exportPeriodValue"/>
        </div>
        <p class="pi-muted pi-ret__lead">{{ leadText }}</p>
        <p v-if="choice.type === 'company'" :id="ids.granularityHint" class="pi-muted pi-ret__lead">{{ companyGranularityText }}</p>

        <pi-data-state :loading="request.loading" :error="request.error" :envelope="request.envelope" :empty="isEmpty"
                       empty-message="Nenhuma coorte no período." :show-footer="false" @retry="load(true)">
          <section class="pi-section" aria-labelledby="pi-ret-matrix">
            <div class="pi-section__head">
              <h2 id="pi-ret-matrix" class="pi-section__title">Matriz de retenção acumulada</h2>
              <p class="pi-section__hint">{{ summary.cohortsText }} · {{ summary.sizeText }} {{ subjectText }}<template v-if="summary.derivedText"> · {{ summary.derivedText }}</template></p>
              <pi-export-menu title="Matriz de retenção acumulada" screen="Retenção" :columns="matrixColumns" :rows="matrixTable" :envelope="request.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="exportDisabled"/>
            </div>
            <pi-cohort-matrix :columns="matrixHeaderColumns" :offsets="matrixOffsets" :cohorts="cohorts" :granularity="choice.granularity" :type="choice.type"/>
            <p class="pi-muted pi-ret__note">
              Cada célula mostra o acumulado (ativo em algum período até ali); o ativo exatamente naquele período aparece ao passar o mouse.
            </p>
          </section>

          <section class="pi-section" aria-labelledby="pi-ret-curve">
            <div class="pi-section__head">
              <h2 id="pi-ret-curve" class="pi-section__title">Curva por coorte</h2>
              <p class="pi-section__hint">As {{ curveLimit }} coortes mais recentes; célula pendente fica fora da curva.</p>
            </div>
            <pi-line-chart :series="curves" unit="pct" :format-label="offsetFormatter" :x-label-count="8"
                           empty-text="Nenhuma célula madura para desenhar a curva."/>
          </section>

          <section class="pi-section" aria-labelledby="pi-ret-compare">
            <div class="pi-section__head">
              <h2 id="pi-ret-compare" class="pi-section__title">Comparação entre coortes</h2>
              <pi-export-menu title="Comparação entre coortes" screen="Retenção" :columns="comparisonColumns" :rows="comparisonTable"
                              :envelope="request.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="exportDisabled"/>
            </div>
            <div class="pi-table-wrap">
              <table class="pi-table pi-ret__table">
                <caption class="pi-sr">Retenção acumulada nos marcos principais por coorte</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in comparisonHeaderColumns" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr class="pi-ret__average">
                  <th scope="row">
                    {{ averageLabel }}
                    <pi-definition-tooltip info-key="retention.weightedAverage" :label="averageLabel"/>
                  </th>
                  <td class="is-num">—</td>
                  <td v-for="cell in comparison.average" :key="'avg' + cell.offset" class="pi-ret__cell" :style="cell.style" :title="cell.title">
                    {{ cell.text }}
                  </td>
                </tr>
                <tr v-for="row in comparison.rows" :key="row.key">
                  <th scope="row">
                    {{ row.label }}
                    <span v-if="row.derived" class="pi-badge pi-badge--info">retroativo</span>
                  </th>
                  <td class="is-num">
                    {{ row.sizeText }}
                    <span v-if="row.companiesText && choice.type !== 'company'" class="pi-table__sub">{{ row.companiesText }}</span>
                  </td>
                  <td v-for="cell in row.cells" :key="row.key + '-' + cell.offset" class="pi-ret__cell"
                      :class="{ 'is-pending': cell.pending }" :style="cell.style" :title="cell.title">{{ cell.text }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </section>
        </pi-data-state>

        <pi-page-footer :envelopes="[request.envelope]"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piCohortMatrix from "../../components/pi/piCohortMatrix.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piTh from "../../components/pi/piTh.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, optionLabel, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import {
  AVERAGE_LABEL, COMPANY_GRANULARITY_TEXT, CURVE_LIMIT, KEY_OFFSETS, RETENTION_COMPARISON_COLUMNS, RETENTION_GRANULARITIES, RETENTION_MATRIX_COLUMNS,
  RETENTION_TYPES, chooseRetentionGranularity, chooseRetentionType, columnOffsets, granularityAllowed, offsetLabelOf, offsetsOf, retentionChoice,
  retentionColumns, retentionComparison, retentionComparisonRows, retentionCurves, retentionInfoKey, retentionMatrixRows, retentionSummary,
} from "../../components/pi/piRetentionModel.js";
import { PI_RETENTION_COHORTS } from "../../../../urlConfig";

const FILTER_FIELDS = ["period"];
const CHOICE_STORAGE_KEY = "pi:retention-choice";

const readChoice = () => {
  try {
    const raw = window.sessionStorage.getItem(CHOICE_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return retentionChoice(saved && saved.type, saved && saved.granularity, saved && saved.userGranularity);
  } catch (e) {
    return retentionChoice();
  }
};

const writeChoice = (choice) => {
  try {
    window.sessionStorage.setItem(CHOICE_STORAGE_KEY, JSON.stringify(choice));
  } catch (e) {
    return;
  }
};

let sequence = 0;

export default {
  name: "pi-retention",
  components: {
    boxDefault, piTabs, piFilters, piDataState, piCohortMatrix, piLineChart, piDefinitionTooltip, piPageFooter, piExportMenu, piExportScreen, piTh,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    sequence += 1;
    return {
      ids: {
        type: "pi-ret-type-" + sequence,
        granularity: "pi-ret-granularity-" + sequence,
        granularityHint: "pi-ret-granularity-hint-" + sequence,
      },
      companyGranularityText: COMPANY_GRANULARITY_TEXT,
      filterFields: FILTER_FIELDS,
      request: blankRequest(),
      types: RETENTION_TYPES,
      granularities: RETENTION_GRANULARITIES,
      choice: readChoice(),
      curveLimit: CURVE_LIMIT,
      averageLabel: AVERAGE_LABEL,
    };
  },
  computed: {
    params() {
      return Object.assign(buildApiQuery(this.filters, { fields: FILTER_FIELDS, extra: { type: this.choice.type } }), {
        granularity: this.choice.granularity,
      });
    },
    apiKey() {
      return toQueryString(this.params);
    },
    payload() {
      return payloadOf(this.request.envelope);
    },
    retentionInfoKey() {
      return retentionInfoKey(this.choice.type);
    },
    definitionVersion() {
      return this.request.envelope ? this.request.envelope.definitionVersion : null;
    },
    cohorts() {
      return listOf(this.payload.cohorts);
    },
    offsets() {
      return offsetsOf(this.payload);
    },
    summary() {
      return retentionSummary(this.cohorts);
    },
    subjectText() {
      return this.choice.type === "company" ? "empresas" : "usuários";
    },
    leadText() {
      if (this.choice.type === "company") {
        return "Coorte de empresas = mês da ativação (1ª batida). Retida = empresa com uso real no período. Denominador: empresas da coorte.";
      }
      return "Coorte de usuários = período da primeira ação significativa. Retido = usuário ativo no período. Denominador: usuários da coorte.";
    },
    curves() {
      return retentionCurves(this.cohorts, this.offsets, CURVE_LIMIT);
    },
    offsetFormatter() {
      return offsetLabelOf(this.choice.granularity);
    },
    comparison() {
      return retentionComparison(this.cohorts, this.choice.granularity);
    },
    matrixColumns() {
      return retentionColumns(RETENTION_MATRIX_COLUMNS, this.choice.type, this.choice.granularity, this.offsets);
    },
    matrixHeaderColumns() {
      return screenColumns(this.matrixColumns);
    },
    matrixOffsets() {
      return columnOffsets(this.matrixColumns);
    },
    matrixTable() {
      return retentionMatrixRows(this.cohorts, this.choice.granularity);
    },
    comparisonColumns() {
      return retentionColumns(RETENTION_COMPARISON_COLUMNS, this.choice.type, this.choice.granularity, KEY_OFFSETS[this.choice.granularity]);
    },
    comparisonHeaderColumns() {
      return screenColumns(this.comparisonColumns);
    },
    comparisonTable() {
      return retentionComparisonRows(this.cohorts, this.choice.granularity);
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    exportFilters() {
      return filterLines({
        filters: this.filters,
        fields: FILTER_FIELDS,
        extra: [
          "Coorte de: " + optionLabel(RETENTION_TYPES, this.choice.type),
          "Granularidade: " + optionLabel(RETENTION_GRANULARITIES, this.choice.granularity),
        ],
      });
    },
    exportDisabled() {
      return !this.request.loaded || !!this.request.error || !this.cohorts.length;
    },
    isEmpty() {
      return this.request.loaded && !this.cohorts.length;
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
  },
  created() {
    this.load(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => piGet(PI_RETENTION_COHORTS, this.params), "carregar as coortes de retenção", key);
    },
    allowed(type, granularity) {
      return granularityAllowed(type, granularity);
    },
    selectType(type) {
      this.setChoice(chooseRetentionType(this.choice, type));
    },
    selectGranularity(granularity) {
      this.setChoice(chooseRetentionGranularity(this.choice, granularity));
    },
    setChoice(choice) {
      this.choice = choice;
      writeChoice(this.choice);
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-ret__lead {
  margin-bottom: 14px;
}

.pi-ret__export {
  margin-left: auto;
}

.pi-ret__note {
  margin-top: 8px;
}

.pi-ret__table {
  border-collapse: separate;
  border-spacing: 2px;
}

.pi-ret__table th[scope="row"] {
  font-weight: 600;
  white-space: nowrap;
}

.pi-ret__cell {
  min-width: 64px;
  border-radius: 3px;
  text-align: center;
  font-weight: 600;
  white-space: nowrap;
}

.pi-ret__cell.is-pending {
  border: 1px dashed #c5cad3;
  background: #ffffff;
  color: #6b7280;
  font-weight: 400;
}

.pi-ret__average th,
.pi-ret__average td {
  border-bottom: 2px solid #c5cad3;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piRetention.vue